import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sparkles, Loader2, Megaphone, Users, Calendar, ShoppingBag, Mail, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface Suggestion {
  category: "communication" | "accompagnements" | "evenements" | "boutique" | "newsletter";
  title: string;
  description: string;
  objective: string;
  timing: string;
  priority: "haute" | "moyenne" | "basse";
}

interface StatisticsData {
  totalAppointments: number;
  totalAllTime: number;
  genderStats: Array<{ name: string; value: number; percentage: string }>;
  ageStats: Array<{ name: string; value: number; percentage: string }>;
  discoveryStats: Array<{ name: string; value: number }>;
  locationStats?: Array<{ name: string; value: number; isVisio: boolean; percentage: string }>;
  visioTotal?: number;
  cabinetTotal?: number;
  monthlyStats: Array<{ name: string; appointments: number }>;
  selectedYear: number;
  giftCardsSold?: number;
  productsSold?: number;
  newsletterSubscribers?: number;
}

interface AISuggestionsPanelProps {
  statistics: StatisticsData;
}

const CATEGORY_CONFIG = {
  communication: {
    label: "Communication",
    icon: Megaphone,
    color: "bg-blue-500/10 text-blue-600 border-blue-200",
  },
  accompagnements: {
    label: "Accompagnements",
    icon: Users,
    color: "bg-green-500/10 text-green-600 border-green-200",
  },
  evenements: {
    label: "Événements",
    icon: Calendar,
    color: "bg-purple-500/10 text-purple-600 border-purple-200",
  },
  boutique: {
    label: "Boutique",
    icon: ShoppingBag,
    color: "bg-orange-500/10 text-orange-600 border-orange-200",
  },
  newsletter: {
    label: "Newsletter",
    icon: Mail,
    color: "bg-pink-500/10 text-pink-600 border-pink-200",
  },
};

const PRIORITY_COLORS = {
  haute: "bg-red-100 text-red-700 border-red-200",
  moyenne: "bg-yellow-100 text-yellow-700 border-yellow-200",
  basse: "bg-green-100 text-green-700 border-green-200",
};

export function AISuggestionsPanel({ statistics }: AISuggestionsPanelProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  const generateSuggestions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("statistics-suggestions", {
        body: { statistics },
      });

      if (error) throw error;

      if (data?.suggestions) {
        setSuggestions(data.suggestions);
        setHasGenerated(true);
        toast.success("Suggestions générées avec succès !");
      } else if (data?.rawContent) {
        toast.info("Réponse reçue, mais format inattendu");
        console.log("Raw content:", data.rawContent);
      } else {
        throw new Error("Format de réponse invalide");
      }
    } catch (error: any) {
      console.error("Error generating suggestions:", error);
      if (error.message?.includes("429")) {
        toast.error("Limite de requêtes atteinte, veuillez réessayer plus tard.");
      } else if (error.message?.includes("402")) {
        toast.error("Crédits insuffisants pour le service IA.");
      } else {
        toast.error("Erreur lors de la génération des suggestions");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          Suggestions IA personnalisées
        </CardTitle>
        <CardDescription>
          L'IA analyse vos statistiques et propose des actions pour développer votre activité
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hasGenerated ? (
          <div className="text-center py-6">
            <Sparkles className="w-12 h-12 mx-auto text-primary/50 mb-4" />
            <p className="text-muted-foreground mb-4">
              Cliquez pour obtenir des suggestions personnalisées basées sur vos statistiques
            </p>
            <Button 
              onClick={generateSuggestions} 
              disabled={loading}
              size="lg"
              className="gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Générer des suggestions
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={generateSuggestions}
                disabled={loading}
                className="gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Régénérer
              </Button>
            </div>

            <Accordion type="single" collapsible className="space-y-2">
              {suggestions.map((suggestion, index) => {
                const config = CATEGORY_CONFIG[suggestion.category];
                const Icon = config?.icon || Sparkles;
                
                return (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="border rounded-lg px-4 bg-card"
                  >
                    <AccordionTrigger className="hover:no-underline py-3">
                      <div className="flex items-center gap-3 text-left flex-1">
                        <div className={`p-2 rounded-lg ${config?.color || ''}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{suggestion.title}</span>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${PRIORITY_COLORS[suggestion.priority]}`}
                            >
                              Priorité {suggestion.priority}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {config?.label} • {suggestion.timing}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2 pb-4">
                      <div className="space-y-3 pl-11">
                        <div>
                          <h4 className="text-sm font-medium text-muted-foreground mb-1">Description</h4>
                          <p className="text-sm">{suggestion.description}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-muted-foreground mb-1">Objectif</h4>
                          <p className="text-sm flex items-center gap-2">
                            <ChevronRight className="w-4 h-4 text-primary" />
                            {suggestion.objective}
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
